import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle, Switch, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import dayjs from "dayjs";
import { Download, Edit2, ExternalLink, FileText, Mail, Send } from "react-feather";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { downloadFile, formatGBP2 } from "../../../components/Features";
import billingStyles from "../../../components/billing.module.css";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { ConfirmDialog, eventText, eventTone, fmtDate, fmtDateTime, MarkPaidDialog, saGet, saPost, StatusChip, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";

const TABS = ["overview", "addons", "invoices", "activity"];

// Confirm dialog text for each company status action
const STATUS_CONFIRM = {
  suspend: { title: "Suspend this company?", message: "The company, its staff, drivers and customers lose access immediately. Only you can lift a manual suspension.", label: "Suspend" },
  activate: { title: "Activate this company?", message: "Access is restored straight away.", label: "Activate" },
  cancel: { title: "End this subscription now?", message: "The subscription ends today: the company loses access to the system and apps, add-ons are switched off and no further invoices are issued. Unpaid invoices are still owed. The company admin can resubscribe later.", label: "End subscription" },
  undo_cancel: { title: "Undo the cancellation?", message: "The company keeps its subscription and is billed as normal.", label: "Undo cancellation" },
  resubscribe: { title: "Resubscribe this company?", message: "The subscription restarts today with no new trial: the company is billed monthly from today and its add-ons start switched off.", label: "Resubscribe" },
};

// Everything about one company: billing state, add-ons, invoices and its timeline
const CompanyDetail = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [extendMode, setExtendMode] = useState("days");
  const [extendDate, setExtendDate] = useState("");
  const [note, setNote] = useState("");
  const [toast, showMessage, closeToast] = useToast();

  const tab = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "overview";

  const { websiteData } = useWebsite();
  useSiteBranding(data?.company?.company_name || "Company", websiteData);

  const load = () => {
    saGet(`companies/${id}`).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") setData(response.data.data);
      else showMessage(response.data.message, "error");
    }).catch(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id]);

  const respond = (response) => {
    setBusy(false);
    setConfirm(null);
    showMessage(response.data.message, response.data.status === "SUCCESS" ? "success" : "error");
    if (response.data.status === "SUCCESS") load();
  };
  const fail = () => { setBusy(false); setConfirm(null); showMessage("Something went wrong. Please try again.", "error"); };

  const runConfirm = () => {
    setBusy(true);
    const c = confirm;
    let req;
    if (c.kind === "status") req = saPost(`companies/${id}/status`, { action: c.action });
    if (c.kind === "generate") req = saPost(`companies/${id}/generate-invoice`);
    if (c.kind === "void") req = saPost(`invoices/${c.invoice.id}/void`);
    req.then(respond).catch(fail);
  };

  const toggleAddon = (addon) => {
    saPost(`companies/${id}/addons/${addon.key}`, { enabled: !addon.enabled }).then(respond).catch(fail);
  };

  const openExtend = () => {
    const base = data?.summary?.trial_ends_at && dayjs(data.summary.trial_ends_at).isAfter(dayjs(), "day") ? dayjs(data.summary.trial_ends_at) : dayjs();
    setExtendMode("days");
    setExtendDays(7);
    setExtendDate(base.add(7, "day").format("YYYY-MM-DD"));
    setExtendOpen(true);
  };

  // New trial end the dialog would set, for the preview and validation
  const newTrialEnd = (() => {
    if (extendMode === "date") return extendDate ? dayjs(extendDate) : null;
    const days = parseInt(extendDays, 10);
    if (!(days > 0)) return null;
    const current = data?.summary?.trial_ends_at ? dayjs(data.summary.trial_ends_at) : dayjs();
    return (current.isBefore(dayjs(), "day") ? dayjs() : current).add(days, "day");
  })();
  const extendValid = !!newTrialEnd && newTrialEnd.isAfter(dayjs(), "day") && newTrialEnd.isBefore(dayjs().add(1, "year").add(1, "day"), "day");
  const leadDays = data?.summary?.next_billing_date && data?.summary?.next_invoice_date ? dayjs(data.summary.next_billing_date).diff(dayjs(data.summary.next_invoice_date), "day") : 3;

  const extendTrial = () => {
    setBusy(true);
    const payload = extendMode === "date" ? { action: "extend_trial", trial_ends_at: extendDate } : { action: "extend_trial", days: extendDays };
    saPost(`companies/${id}/status`, payload).then((r) => { setExtendOpen(false); respond(r); }).catch(fail);
  };

  const resend = (inv) => saPost(`invoices/${inv.id}/resend`).then(respond).catch(fail);

  const addNote = () => {
    if (!note.trim()) return;
    saPost(`companies/${id}/notes`, { note }).then((r) => { setNote(""); respond(r); }).catch(fail);
  };

  const company = data?.company;
  const summary = data?.summary;
  const usage = data?.usage || {};
  const status = company?.billing_status;

  return (<div className={`${styles.Page}`}>
    <PageHeader title={company?.company_name || "Company"} trail={[{ label: "Companies", to: "/super-admin/companies" }]} />

    {loading && <TableSkeleton rows={5} kpis={4} />}

    {!loading && company && (<>
      <div className={`${styles.DetailHead}`}>
        <div className={`${styles.DetailAvatar}`}>{(company.company_name || "?").charAt(0).toUpperCase()}</div>
        <div>
          <p className={`${styles.DetailTitle}`}>{company.company_name} <StatusChip status={status} /></p>
          <p className={`${styles.DetailMeta}`}>
            {company.domain ? <a href={`${window.location.protocol}//${company.domain}`} target="_blank" rel="noreferrer">{company.domain} <ExternalLink size={12} /></a> : "No domain"}
            {" · "}Joined {fmtDate(company.created_at)}
            {status === "suspended" && ` · Suspended ${fmtDate(company.suspended_at)} (${company.suspended_reason === "manual" ? "by admin" : "overdue"})`}
            {status === "cancelled" && ` · Subscription ended ${fmtDate(company.cancels_at)}`}
          </p>
        </div>
        <div className={`${styles.DetailActions}`}>
          <Link to={`/super-admin/companies/${id}/edit`} className={`${styles.ActionBU}`}><Edit2 />Edit</Link>
          {status === "trial" && <button type="button" className={`${styles.ActionBU}`} onClick={openExtend}>Extend trial</button>}
          {status !== "cancelled" && <button type="button" className={`${styles.ActionBU}`} onClick={() => setConfirm({ kind: "generate" })}><FileText />Generate invoice now</button>}
          {status === "cancelled" && <button type="button" className={`${styles.ActionBU} ${styles.ActionPrimary}`} onClick={() => setConfirm({ kind: "status", action: "resubscribe" })}>Resubscribe</button>}
          {status === "suspended" && <button type="button" className={`${styles.ActionBU} ${styles.ActionPrimary}`} onClick={() => setConfirm({ kind: "status", action: "activate" })}>Activate</button>}
          {(status === "trial" || status === "active") && <button type="button" className={`${styles.ActionBU} ${styles.ActionDanger}`} onClick={() => setConfirm({ kind: "status", action: "suspend" })}>Suspend</button>}
          {status !== "cancelled" && <button type="button" className={`${styles.ActionBU} ${styles.ActionDanger}`} onClick={() => setConfirm({ kind: "status", action: "cancel" })}>End subscription</button>}
        </div>
      </div>

      {company.cancels_at && status !== "cancelled" && <div className={`${styles.CancelNotice}`}>
        <div>
          <strong>Unsubscribed on {fmtDate(company.cancel_requested_at)}. Service ends {fmtDate(company.cancels_at)}.</strong>
          <span className={`${styles.SubText}`}>{company.cancel_reason ? `Reason: ${company.cancel_reason}` : "No reason given."} {status === "trial" ? "No invoice will be issued." : "The final invoice for the period ending that day is still issued."}</span>
        </div>
        <button type="button" className={`${styles.ActionBU}`} onClick={() => setConfirm({ kind: "status", action: "undo_cancel" })}>Undo cancellation</button>
      </div>}

      <div className={`${styles.KpiGrid}`}>
        <div className={`${styles.Kpi}`}><span className={`${styles.KpiLabel}`}>Monthly estimate</span><span className={`${styles.KpiValue}`}>{formatGBP2(summary?.monthly_estimate)}</span><span className={`${styles.KpiSub}`}>Basic {formatGBP2(summary?.basic_price)} + {summary?.addons?.length || 0} add-ons</span></div>
        <div className={`${styles.Kpi}`}><span className={`${styles.KpiLabel}`}>{status === "trial" ? "Trial ends" : "Next billing date"}</span><span className={`${styles.KpiValue}`}>{status === "trial" ? fmtDate(summary?.trial_ends_at) : fmtDate(summary?.next_billing_date)}</span><span className={`${styles.KpiSub}`}>Invoice emailed {fmtDate(summary?.next_invoice_date)}</span></div>
        <div className={`${styles.Kpi} ${(summary?.outstanding_invoices || []).some((i) => i.status === "overdue") ? styles.KpiDanger : ""}`}><span className={`${styles.KpiLabel}`}>Outstanding</span><span className={`${styles.KpiValue}`}>{formatGBP2((summary?.outstanding_invoices || []).reduce((s, i) => s + Number(i.total || 0), 0))}</span><span className={`${styles.KpiSub}`}>{(summary?.outstanding_invoices || []).length} open invoice(s)</span></div>
        <div className={`${styles.Kpi}`}><span className={`${styles.KpiLabel}`}>Bookings this month</span><span className={`${styles.KpiValue}`}>{usage.bookings_this_month}</span><span className={`${styles.KpiSub}`}>{usage.users} users · {usage.drivers} drivers · {usage.customers} customers</span></div>
      </div>

      <Tabs className={`${styles.Tabs}`} value={tab} onChange={(e, v) => setSearchParams(v === "overview" ? {} : { tab: v }, { replace: true })} variant="scrollable" allowScrollButtonsMobile>
        <Tab value="overview" label="Overview" />
        <Tab value="addons" label={`Add-ons (${(data.addons || []).filter((a) => a.enabled).length})`} />
        <Tab value="invoices" label={`Invoices (${(data.invoices || []).length})`} />
        <Tab value="activity" label="Activity" />
      </Tabs>

      {tab === "overview" && (<div className={`${styles.GridHalf}`}>
        <div className={`${styles.Panel}`}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 14 }}>Company</p>
          <dl className={`${styles.DefList}`}>
            <div><dt>Owner</dt><dd>{data.owner?.name || company.name}</dd></div>
            <div><dt>Owner email</dt><dd><a href={`mailto:${data.owner?.email || company.email}`}>{data.owner?.email || company.email}</a></dd></div>
            <div><dt>Phone</dt><dd>{company.phone || "—"}</dd></div>
            <div><dt>Address</dt><dd>{company.company_address || "—"}</dd></div>
            <div><dt>Route</dt><dd>{company.pickup_country || "?"} → {company.delivery_country || "?"}</dd></div>
            <div><dt>VAT / Reg no.</dt><dd>{company.vat_number || "—"} / {company.reg_number || "—"}</dd></div>
          </dl>
        </div>
        <div className={`${styles.Panel}`}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 14 }}>Billing</p>
          <dl className={`${styles.DefList}`}>
            <div><dt>Status</dt><dd><StatusChip status={status} /></dd></div>
            <div><dt>Trial ends</dt><dd>{fmtDate(company.trial_ends_at)}</dd></div>
            <div><dt>Next billing date</dt><dd>{fmtDate(company.next_billing_date)}</dd></div>
            <div><dt>Last invoiced</dt><dd>{fmtDate(company.last_invoiced_at)}</dd></div>
            <div><dt>Active add-ons</dt><dd>{(summary?.addons || []).map((a) => a.name).join(", ") || "None"}</dd></div>
            <div><dt>Monthly estimate</dt><dd>{formatGBP2(summary?.monthly_estimate)}</dd></div>
          </dl>
        </div>
      </div>)}

      {tab === "addons" && (<div className={`${styles.GridHalf}`}>
        <div className={`${styles.Panel}`}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 8 }}>Add-ons</p>
          <p className={`${styles.Muted}`} style={{ marginTop: 0 }}>Changes apply to the company immediately and are billed on its next invoice.</p>
          {(data.addons || []).map((a) => (<div className={`${styles.AddonRow}`} key={a.key}>
            <div>
              <strong>{a.name}</strong>
              <span className={`${styles.SubText}`}>{formatGBP2(a.monthly_price)}/month{a.enabled && a.enabled_at ? ` · since ${fmtDate(a.enabled_at)}` : ""}</span>
            </div>
            <Switch style={{ marginLeft: "auto" }} checked={!!a.enabled} onChange={() => toggleAddon(a)} inputProps={{ "aria-label": `Toggle ${a.name}` }} />
          </div>))}
        </div>
        <div className={`${styles.Panel}`}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 14 }}>Add-on history</p>
          {(data.addon_history || []).length === 0 && <p className={`${styles.EmptyNote}`}>No add-ons have been used yet.</p>}
          <ul className={`${styles.Timeline}`}>
            {(data.addon_history || []).map((h) => (<li key={h.id} className={h.disabled_at ? styles.TimelineBad : styles.TimelineGood}>
              <span className={`${styles.TimelineText}`}>{h.name}</span>
              <span className={`${styles.SubText}`}>{fmtDate(h.enabled_at)} → {h.disabled_at ? fmtDate(h.disabled_at) : "active"}</span>
            </li>))}
          </ul>
        </div>
      </div>)}

      {tab === "invoices" && (<div className={`${styles.TableWrap}`}>
        <table className={`${styles.Table}`}>
          <thead><tr><th>Invoice</th><th>Period</th><th>Due</th><th className={`${styles.Right}`}>Total</th><th>Status</th><th>Emailed</th><th></th></tr></thead>
          <tbody>
            {(data.invoices || []).map((inv) => (<tr key={inv.id}>
              <td><Link to={`/super-admin/invoices/${inv.id}`} className={`${styles.RowLink}`}>{inv.invoice_number}</Link><span className={`${styles.SubText}`}>Issued {fmtDate(inv.issue_date)}</span></td>
              <td>{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
              <td>{fmtDate(inv.due_date)}{inv.status === "overdue" && <span className={`${styles.SubText} ${styles.Danger}`}>Suspends after {fmtDate(inv.suspend_on)}</span>}</td>
              <td className={`${styles.Right}`}>{formatGBP2(inv.total)}</td>
              <td><StatusChip status={inv.status} />{inv.status === "paid" && <span className={`${styles.SubText}`}>{fmtDate(inv.paid_at)}{inv.payment_reference ? ` · ${inv.payment_reference}` : ""}</span>}</td>
              <td>{inv.emailed_at ? fmtDate(inv.emailed_at) : <span className={`${styles.Danger}`} title={inv.email_error}>{inv.email_error ? "Failed" : "Not sent"}</span>}</td>
              <td>
                <div className={`${styles.Actions}`}>
                  {(inv.status === "pending" || inv.status === "overdue") && <button type="button" className={`${styles.ActionBU} ${styles.ActionPrimary}`} onClick={() => setPayInvoice({ ...inv, company_name: company.company_name })}>Mark paid</button>}
                  <button type="button" className={`${styles.ActionBU}`} onClick={() => resend(inv)} aria-label="Resend email"><Send /></button>
                  <button type="button" className={`${styles.ActionBU}`} onClick={() => downloadFile(`v1/admin/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`)} aria-label="Download PDF"><Download /></button>
                  {inv.status !== "paid" && inv.status !== "void" && <button type="button" className={`${styles.ActionBU} ${styles.ActionDanger}`} onClick={() => setConfirm({ kind: "void", invoice: inv })}>Void</button>}
                </div>
              </td>
            </tr>))}
            {(data.invoices || []).length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", padding: 30 }}>No invoices yet. The first one is emailed on {fmtDate(summary?.next_invoice_date)}.</td></tr>}
          </tbody>
        </table>
      </div>)}

      {tab === "activity" && (<div className={`${styles.Panel}`}>
        <div className={`${styles.NoteBox}`}>
          <TextField size="small" placeholder="Add a note (e.g. called about overdue invoice)" value={note} onChange={(e) => setNote(e.target.value)} multiline maxRows={4} />
          <button type="button" className={`${billingStyles.PrimaryBU}`} onClick={addNote} disabled={!note.trim()}><Mail size={16} />Add note</button>
        </div>
        {(data.events || []).length === 0 && <p className={`${styles.EmptyNote}`}>No activity yet.</p>}
        <ul className={`${styles.Timeline}`}>
          {(data.events || []).map((e) => (<li key={e.id} className={eventTone(e.type)}>
            <span className={`${styles.TimelineText}`}>{eventText(e)}</span>
            <span className={`${styles.SubText}`}>{e.actor_name || "System"} · {fmtDateTime(e.created_at)}{e.invoice_number ? <> · <Link to={`/super-admin/invoices/${e.invoice_id}`}>{e.invoice_number}</Link></> : null}</span>
          </li>))}
        </ul>
      </div>)}
    </>)}

    <ConfirmDialog
      open={confirm}
      busy={busy}
      title={confirm?.kind === "generate" ? "Generate the next invoice now?" : confirm?.kind === "void" ? `Void ${confirm?.invoice?.invoice_number}?` : (STATUS_CONFIRM[confirm?.action] || {}).title}
      message={confirm?.kind === "generate"
        ? `An invoice for the period ending ${fmtDate(summary?.next_billing_date)} is created and emailed to the company now, and the billing date moves on a month.`
        : confirm?.kind === "void"
          ? "The invoice is cancelled and no longer counts as owed. If it was the only overdue invoice, an overdue suspension is lifted."
          : (STATUS_CONFIRM[confirm?.action] || {}).message}
      confirmLabel={confirm?.kind === "generate" ? "Generate & email" : confirm?.kind === "void" ? "Void invoice" : (STATUS_CONFIRM[confirm?.action] || {}).label}
      onConfirm={runConfirm}
      onClose={() => setConfirm(null)}
    />

    <Dialog open={extendOpen} onClose={() => !busy && setExtendOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Extend trial</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>Currently ends {fmtDate(summary?.trial_ends_at)}. The first invoice and billing dates move with the trial end.</p>
        <ToggleButtonGroup exclusive size="small" value={extendMode} onChange={(e, v) => v && setExtendMode(v)} sx={{ mb: 1 }}>
          <ToggleButton value="days">Add days</ToggleButton>
          <ToggleButton value="date">Pick end date</ToggleButton>
        </ToggleButtonGroup>
        {extendMode === "days"
          ? <TextField type="number" label="Extra days" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} fullWidth size="small" margin="dense" inputProps={{ min: 1, max: 365 }} />
          : <TextField type="date" label="New trial end date" value={extendDate} onChange={(e) => setExtendDate(e.target.value)} fullWidth size="small" margin="dense" InputLabelProps={{ shrink: true }} inputProps={{ min: dayjs().add(1, "day").format("YYYY-MM-DD"), max: dayjs().add(1, "year").format("YYYY-MM-DD") }} />}
        {extendValid
          ? <p className={`${styles.Example}`}>Trial ends <strong>{newTrialEnd.format("DD MMM YYYY")}</strong> → first invoice emailed <strong>{newTrialEnd.add(1, "month").subtract(leadDays, "day").format("DD MMM YYYY")}</strong> → due <strong>{newTrialEnd.add(1, "month").format("DD MMM YYYY")}</strong>{company?.cancels_at ? ". The pending cancellation moves to the new trial end." : "."}</p>
          : <p className={`${styles.Danger}`} style={{ fontSize: 13 }}>Choose an end date after today and within a year.</p>}
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 20px" }}>
        <button type="button" className={`${billingStyles.SecondaryBU}`} onClick={() => setExtendOpen(false)} disabled={busy}>Cancel</button>
        <button type="button" className={`${billingStyles.PrimaryBU}`} onClick={extendTrial} disabled={busy || !extendValid}>Save trial end</button>
      </DialogActions>
    </Dialog>

    <MarkPaidDialog invoice={payInvoice} onClose={() => setPayInvoice(null)} onDone={load} showMessage={showMessage} />
    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(CompanyDetail);
